import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import {
  Users, Building2, Clock3, UserCog, Repeat, Smartphone, ClipboardCheck, Activity,
} from "lucide-react";

const KPI_TILE = "bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition";

function Tile({ icon: Icon, label, value, sub, testid, onClick, accent = "slate" }) {
  const colors = {
    slate:   "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    sky:     "bg-sky-50 text-sky-700",
    violet:  "bg-violet-50 text-violet-700",
    rose:    "bg-rose-50 text-rose-700",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`${KPI_TILE} text-left w-full ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[accent] || colors.slate}`}>
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.75}/>
        </div>
      </div>
      <div className="text-[13px] text-slate-500 mb-1">{label}</div>
      <div className="font-display text-[28px] leading-none font-bold text-hocp-navy tracking-tight" data-testid={`${testid}-value`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1.5">{sub}</div>}
    </button>
  );
}

export default function WorkforceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    wfAPI.dashboard()
      .then((d) => mounted && setData(d))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-6" data-testid="wf-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Users}         label="Employees Active"       value={data?.employees_active} sub={data ? `${data.employees_inactive} inactive · ${data.employees_total} total` : ""} testid="tile-employees" accent="slate" onClick={() => nav("/wf/employees")} />
        <Tile icon={Building2}     label="Departments"            value={data?.org_units_count} sub="Org units" testid="tile-org-units" accent="sky" onClick={() => nav("/wf/organization")} />
        <Tile icon={Clock3}        label="Temporary Access"       value={data?.temporary_permissions_active} sub="Active grants" testid="tile-temporary" accent="amber" onClick={() => nav("/wf/permissions")} />
        <Tile icon={UserCog}       label="Acting Roles"           value={data?.acting_roles_active} sub="Time-bound overrides" testid="tile-acting" accent="violet" onClick={() => nav("/wf/permissions")} />
        <Tile icon={Repeat}        label="Delegations"            value={data?.delegations_active} sub="Approval routings" testid="tile-delegations" accent="emerald" onClick={() => nav("/wf/permissions")} />
        <Tile icon={Smartphone}    label="Devices"                value={data?.devices_total} sub={data ? `${data.device_pairings_active} paired` : ""} testid="tile-devices" accent="rose" onClick={() => nav("/wf/devices")} />
        <Tile icon={ClipboardCheck} label="Approvals Pending"     value={data?.approvals_pending} sub="Queue depth" testid="tile-approvals" accent="amber" onClick={() => nav("/wf/approvals")} />
        <Tile icon={Activity}      label="Joining Today"          value={data?.joining_today} sub="New hires" testid="tile-joining" accent="emerald" />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Department Distribution</h2>
            <span className="text-[11px] uppercase text-slate-400 tracking-wide">Active employees</span>
          </div>
          {loading ? (
            <div className="text-slate-400 text-[13px]">Loading…</div>
          ) : !data?.department_distribution?.length ? (
            <div className="text-slate-400 text-[13px]">No departments yet.</div>
          ) : (
            <div className="space-y-2.5">
              {data.department_distribution.map((d) => {
                const total = data.employees_active || 1;
                const pct = Math.min(100, Math.round((d.count / total) * 100));
                return (
                  <div key={d.id} data-testid={`dept-row-${d.id}`}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="text-slate-700 font-medium">{d.name}</span>
                      <span className="text-slate-400 tabular-nums">{d.count}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-hocp-navy" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
